// featuredTourPrefs.ts
// =====================
//
// Wave 14cd: Per-User Sichtbarkeits-/Marker-Prefs für Featured Tours.
//
// Featured Tours selbst sind shared (server-authoritativ — siehe
// featuredTours.ts), aber JEDER USER entscheidet auf SEINEM GERÄT, welche
// davon dauerhaft auf der Karte angezeigt werden sollen. Das ist die client-
// seitige Sidecar-Tabelle die diese Auswahl persistiert.
//
// Schema-Parallele zu customTrails.showOnMap / kmMarkers, nur als separater
// IDB-Store damit wir Tour-Metadaten (Name, GPX, etc.) NICHT lokal
// dupliziert halten — die kommen weiterhin frisch vom Server.

import { getDb } from './storage/db';

export const FEATURED_TOUR_PREFS_UPDATED_EVENT =
  'adt:featured-tour-prefs-updated';

export type KmMarkers = 'off' | '1km' | '10km';

export interface FeaturedTourPrefs {
  tourId: string;
  showOnMap: boolean;
  kmMarkers?: KmMarkers;
  updatedAt: string;
}

function dispatchUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(FEATURED_TOUR_PREFS_UPDATED_EVENT));
  } catch {
    /* SSR */
  }
}

/** Liefert die Prefs für eine einzelne Tour. `null` heisst "kein Eintrag";
 *  Caller sollte das als "default = nicht sichtbar" interpretieren. */
export async function getFeaturedTourPrefs(
  tourId: string,
): Promise<FeaturedTourPrefs | null> {
  try {
    const db = await getDb();
    const row = await db.get('featuredTourPrefs', tourId);
    return row ?? null;
  } catch (err) {
    console.warn('[featuredTourPrefs] get failed', err);
    return null;
  }
}

/** Alle Tours zurückgeben für die der User Prefs gesetzt hat. Map<tourId, prefs>
 *  damit Caller per O(1) lookup machen können. */
export async function listFeaturedTourPrefs(): Promise<Map<string, FeaturedTourPrefs>> {
  try {
    const db = await getDb();
    const all = await db.getAll('featuredTourPrefs');
    return new Map(all.map((p) => [p.tourId, p]));
  } catch (err) {
    console.warn('[featuredTourPrefs] list failed', err);
    return new Map();
  }
}

export async function setFeaturedTourShowOnMap(
  tourId: string,
  showOnMap: boolean,
): Promise<FeaturedTourPrefs> {
  const db = await getDb();
  const existing = (await db.get('featuredTourPrefs', tourId)) ?? null;
  const next: FeaturedTourPrefs = {
    tourId,
    showOnMap,
    kmMarkers: existing?.kmMarkers ?? 'off',
    updatedAt: new Date().toISOString(),
  };
  await db.put('featuredTourPrefs', next);
  dispatchUpdated();
  return next;
}

export async function setFeaturedTourKmMarkers(
  tourId: string,
  value: KmMarkers,
): Promise<FeaturedTourPrefs> {
  const db = await getDb();
  const existing = (await db.get('featuredTourPrefs', tourId)) ?? null;
  const next: FeaturedTourPrefs = {
    tourId,
    showOnMap: existing?.showOnMap ?? true,
    kmMarkers: value,
    updatedAt: new Date().toISOString(),
  };
  await db.put('featuredTourPrefs', next);
  dispatchUpdated();
  return next;
}

export async function deleteFeaturedTourPrefs(tourId: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete('featuredTourPrefs', tourId);
    dispatchUpdated();
  } catch (err) {
    console.warn('[featuredTourPrefs] delete failed', err);
  }
}

/** Carousel-Helper: nächste Stufe (off → 1km → 10km → off). */
export function nextKmMarkerState(
  current: KmMarkers | undefined,
): KmMarkers {
  switch (current) {
    case '1km':
      return '10km';
    case '10km':
      return 'off';
    case 'off':
    default:
      return '1km';
  }
}
